import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, MapPin, Package, Save, User, Sparkles } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import apiClient, { formatApiErrorDetail } from "@/utils/api";
import { PAKISTAN_CITIES } from "@/constants/pakistanCities";

export default function MyAccount() {
  const { customer, loading, isSignedIn, signInWithGoogle, logout, setCustomer } = useCustomerAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    name: customer?.name || "",
    saved_mobile: customer?.saved_mobile || "",
    saved_whatsapp: customer?.saved_whatsapp || "",
    saved_city: customer?.saved_city || "",
    saved_address: customer?.saved_address || "",
  }));

  // Keep the local form in sync when the customer object first arrives from context
  React.useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name || "",
        saved_mobile: customer.saved_mobile || "",
        saved_whatsapp: customer.saved_whatsapp || "",
        saved_city: customer.saved_city || "",
        saved_address: customer.saved_address || "",
      });
    }
  }, [customer]);

  if (loading) {
    return <div className="text-center py-24 text-stone-500 text-sm">Loading your account…</div>;
  }

  if (!isSignedIn) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#996515] text-[11px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Anwer Fancy Lace</span>
        </div>
        <h1 className="font-heading text-3xl font-bold text-stone-900">Sign in to your account</h1>
        <p className="text-sm text-stone-500">
          Save your delivery address, view your order history, and check out faster on future visits.
        </p>
        <button
          type="button"
          onClick={signInWithGoogle}
          data-testid="account-signin-google-btn"
          className="mx-auto flex items-center gap-3 px-6 py-3 bg-white border border-stone-300 hover:border-[#D4AF37] rounded-xl shadow-sm hover:shadow transition font-semibold text-sm text-stone-800"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          <span>Continue with Google</span>
        </button>
        <p className="text-[10px] text-stone-400">
          We use Emergent-managed Google sign-in. Admins log in separately via{" "}
          <Link to="/admin/login" className="underline">the admin portal</Link>.
        </p>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiClient.put("/customer/profile", form);
      setCustomer(data.user);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {customer.picture ? (
            <img src={customer.picture} alt="" className="w-14 h-14 rounded-full border border-[#E5E0D8]" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#1C1917] text-[#D4AF37] flex items-center justify-center font-bold text-lg">
              {(customer.name || customer.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 data-testid="account-user-name" className="font-heading text-2xl font-bold text-stone-900 leading-tight">
              {customer.name || "Anwer Fancy Lace Customer"}
            </h1>
            <div data-testid="account-user-email" className="text-xs text-stone-500">{customer.email}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/my-orders"
            data-testid="account-my-orders-link"
            className="px-4 py-2 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl transition flex items-center gap-2"
          >
            <Package className="w-4 h-4" />
            My Orders
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid="account-logout-btn"
            className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-800 text-xs font-bold rounded-xl transition flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Saved delivery info */}
      <form onSubmit={handleSave} className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E5E0D8] shadow-luxury space-y-5">
        <div className="flex items-center gap-2 pb-2 border-b border-[#E5E0D8]">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="font-heading text-lg font-bold text-stone-900">Saved delivery details</h2>
        </div>
        <p className="text-xs text-stone-500">
          These fields auto-fill at checkout so you can order in one tap.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="space-y-1">
            <label className="font-bold uppercase tracking-wider text-stone-800 text-[11px]">Name on Package</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="account-form-name"
              className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
            />
          </div>
          <div className="space-y-1">
            <label className="font-bold uppercase tracking-wider text-stone-800 text-[11px]">City</label>
            <select
              value={form.saved_city}
              onChange={(e) => setForm({ ...form, saved_city: e.target.value })}
              data-testid="account-form-city"
              className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
            >
              <option value="">Select city…</option>
              {PAKISTAN_CITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="font-bold uppercase tracking-wider text-stone-800 text-[11px]">Mobile</label>
            <input
              type="tel"
              value={form.saved_mobile}
              onChange={(e) => setForm({ ...form, saved_mobile: e.target.value })}
              placeholder="03XX XXXXXXX"
              data-testid="account-form-mobile"
              className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
            />
          </div>
          <div className="space-y-1">
            <label className="font-bold uppercase tracking-wider text-stone-800 text-[11px]">WhatsApp</label>
            <input
              type="tel"
              value={form.saved_whatsapp}
              onChange={(e) => setForm({ ...form, saved_whatsapp: e.target.value })}
              placeholder="03XX XXXXXXX"
              data-testid="account-form-whatsapp"
              className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg"
            />
          </div>
          <div className="sm:col-span-2 space-y-1">
            <label className="font-bold uppercase tracking-wider text-stone-800 text-[11px]">Delivery Address</label>
            <textarea
              rows={3}
              value={form.saved_address}
              onChange={(e) => setForm({ ...form, saved_address: e.target.value })}
              data-testid="account-form-address"
              placeholder="House / Flat, Street, Area, Landmark"
              className="w-full px-3 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="account-save-btn"
            className="px-6 py-2.5 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl transition flex items-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save Details"}
          </button>
        </div>
      </form>
    </div>
  );
}
